/**
 * Shared vocabulary for the texting-agent surfaces.
 *
 * The agent now shows up in three places — the console (queues + workspace +
 * live grid), the lead page's draft dock, and the escalation drawer — and a
 * draft has to look and behave identically in all of them. A recruiter who
 * learns "Approve" on the lead page must not meet a different button in the
 * console. These constants are the seam that keeps that true.
 */

/** Mirrors the tick's DRAFT_SLA_MS — the 10-minute draft_wait threshold the
 *  countdown on every draft card races against. */
export const DRAFT_SLA_MS = 10 * 60_000;

// ---------------------------------------------------------------------------
// Buttons. 44px touch targets on phones only (the CRM density bar); `sm:` and
// up they collapse to the compact desktop size.
// ---------------------------------------------------------------------------

export const btnBase = 'inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 sm:min-h-0';
export const btnPrimary = `${btnBase} bg-primary-600 text-white hover:bg-primary-700`;
export const btnGhost = `${btnBase} border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700`;
export const btnDangerGhost = `${btnBase} border border-gray-300 text-red-600 hover:bg-red-50 dark:border-gray-600 dark:text-red-400 dark:hover:bg-red-900/20`;
export const btnDanger = `${btnBase} bg-red-600 text-white hover:bg-red-700`;
export const iconBtn = 'inline-flex h-11 w-11 items-center justify-center rounded-lg border border-gray-300 text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:h-8 sm:w-8';

export const STATUS_PILL: Record<string, string> = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  paused: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  awaiting_review: 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300',
  handed_off: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  closed: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
};

/** Who said it, for thread badges. */
export const SENDER_LABEL: Record<string, string> = {
  agent: 'Agent', human: 'Team', automation: 'Auto', lead: 'Lead',
};

// ---------------------------------------------------------------------------
// Time
// ---------------------------------------------------------------------------

/** Compact "how long ago" — 'now' / '4m' / '3h' / '2d'. */
export const ago = (iso: string) => {
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  return hrs < 24 ? `${hrs}h` : `${Math.floor(hrs / 24)}d`;
};

/** Future-tense sibling of ago() for booked-call chips. */
export const inFuture = (iso: string) => {
  const mins = Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 60000));
  if (mins < 1) return 'now';
  if (mins < 60) return `in ${mins}m`;
  const hrs = Math.floor(mins / 60);
  return hrs < 24 ? `in ${hrs}h` : `in ${Math.floor(hrs / 24)}d`;
};

export const timeShort = (iso: string) => new Date(iso).toLocaleString(undefined, {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
});

/**
 * SECONDS-resolution elapsed, for the writing pulse only. ago() bottoms out at
 * 'now' for a whole minute, which is the entire lifetime of a normal model
 * call — the one place the coarse clock is useless.
 */
export const elapsedShort = (iso: string, nowTs: number) => {
  const secs = Math.max(0, Math.round((nowTs - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
};

// ---------------------------------------------------------------------------
// Shapes shared across surfaces
// ---------------------------------------------------------------------------

/** A turn awaiting a human: `drafted` (normal copilot) or `suppressed` (the
 *  guardrail wall refused it; approving is the human taking responsibility). */
export interface DraftRow {
  id: string;
  conversation_id: string;
  contact_id?: string | null;
  contact_name?: string | null;
  status?: string;
  draft_body: string;
  validator_results: any;
  created_at: string;
  /** The turn's parsed actions (the draft queue returns them); `actions.sched`
   *  marks a scheduler-mode turn (the texting agent's scheduling assistant). */
  actions?: { sched?: { outcome?: string | null } | null; [k: string]: unknown } | null;
}

/** Is this draft from the texting agent's scheduling assistant (scheduler mode)? */
export const isSchedulerDraft = (t: Pick<DraftRow, 'actions'>): boolean =>
  Boolean(t.actions && typeof t.actions === 'object' && t.actions.sched && typeof t.actions.sched === 'object');

/** One row of a rendered SMS thread (console workspace, escalation drawer). */
export interface ThreadMessage {
  id: string;
  channel: string;
  direction: string | null;
  body: string | null;
  occurred_at: string;
  sender: 'lead' | 'agent' | 'human' | 'automation' | 'system';
  agent_turn_id?: string | null;
  event?: string | null;
  /** Escalation-context reads only: this is the message that caused the flag. */
  is_trigger?: boolean;
}
